// ============================================================================
// TopBarCool — top bar del Layout "cool" (Navegación C, hombro grande).
//
// Estructura visual:
//   ┌──────────┬──────────────────────────────────────────────────────┐
//   │  [S]     │   [🔍 Buscar...]            [🔔][PW][PN]              │ 72px
//   │ Salonika │                                                       │
//   └──────────┘                                                       │
//   sidebar         contenido (curva 56px en esquina superior-izq)
//
// La "rodilla" (left 236px) contiene logo + Salonika; el resto del top bar
// contiene search button + cluster derecho. Sobre gradient azul.
// ============================================================================
import { useEffect, useRef, useState } from 'react';
import {
  Bell, Store, ChevronDown, LogOut, RefreshCw,
  Maximize2, Minimize2, Building2,
} from 'lucide-react';
import { IndicadorConexion } from './indicador-conexion';
import { useUser, QUICK_LOGIN_CREDS } from '../context/user-context';
import { useRegional, useI18n } from '../context/regional-context';
import { IDIOMAS, type Clave } from '../../i18n/idiomas';
import { GlobalSearch } from './ui/global-search';
import { TomSalonikaLockup, SalonikaTomIcon } from './ui/brand-logo';
import type { InventoryAlert, ReservaAlert } from '../hooks/use-notifications';

interface TopBarCoolProps {
  currentBranch: string;
  onBranchChange: (branch: string) => void;
  branches: string[];
  onLogout: () => void;
  notifications?: InventoryAlert[];
  onDismissNotification?: (id: number) => void;
  onDismissAllNotifications?: () => void;
  onGoToInventory?: () => void;
  reservaAlerts?: ReservaAlert[];
  onDismissReserva?: (id: number) => void;
  onGoToReservations?: () => void;
  /** Si true, oculta el codo (sidebar mobile cerrada). */
  hideElbow?: boolean;
  /** Click en el logo en mobile (típico: abre la tab bar "Más" o el drawer). */
  onMobileLogoTap?: () => void;
  /** Si true en desktop, el codo se reduce a 72px (sidebar colapsada). */
  sidebarCollapsed?: boolean;
}

// Rol → clave del diccionario (el texto vive en i18n/diccionarios).
const ROLE_LABEL: Record<string, Clave> = {
  gerente_operativo: 'rol.gerente_operativo',
  gerente_administrativo: 'rol.gerente_administrativo',
  jefe_operaciones: 'rol.jefe_operaciones',
  jefe_administrativo: 'rol.jefe_administrativo',
  contador: 'rol.contador',
  cajera: 'rol.cajera',
  mesero: 'rol.mesero',
  desarrollador: 'rol.desarrollador',
};

function initials(name?: string) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function branchLabel(branch?: string) {
  if (!branch) return '';
  if (branch === 'Todas las sedes') return branch;
  // Strip city prefix ("Bogotá - Park Way" → "Park Way").
  return branch.replace(/^[^-]+\s*-\s*/, '').trim() || branch;
}

// Iniciales cortas para el chip de sede en mobile (jun-2026).
// "Bogotá - Park Way" → "PW"; "Todas las sedes" → "•••".
function branchInitials(branch?: string) {
  if (!branch) return '•';
  if (branch === 'Todas las sedes') return '•••';
  const short = branchLabel(branch);
  const tokens = short.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return short.slice(0, 2).toUpperCase();
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
}

export function TopBarCool({
  currentBranch, onBranchChange, branches, onLogout,
  notifications = [], onDismissNotification, onDismissAllNotifications,
  onGoToInventory,
  reservaAlerts = [], onGoToReservations, onDismissReserva,
  hideElbow = false,
  onMobileLogoTap,
  sidebarCollapsed = false,
}: TopBarCoolProps) {
  const { user, login, logout } = useUser();
  const { t, operaColombia, moneda } = useRegional();

  const [userOpen, setUserOpen] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const userRef = useRef<HTMLDivElement>(null);
  const branchRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const isDev = import.meta.env.DEV;
  const canChangeBranch = user?.branch === 'Todas las sedes';
  const unread = notifications.length + reservaAlerts.length;
  // Hay una reserva próxima a menos de 60 min (futuro o ya empezando).
  // Si la hay, el dot del bell parpadea para llamar la atención.
  const urgentReserva = reservaAlerts.some(r => r.minutosHasta <= 60 && r.minutosHasta >= -10);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) { setUserOpen(false); setRoleOpen(false); }
      if (branchRef.current && !branchRef.current.contains(e.target as Node)) setBranchOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    const sync = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch { /* requires https + gesture */ }
  };

  const switchRole = async (id: string) => {
    const creds = QUICK_LOGIN_CREDS[id];
    if (!creds) return;
    try {
      await logout().catch(() => {});
      await login(creds.username, creds.password);
      setUserOpen(false); setRoleOpen(false);
    } catch { /* noop */ }
  };

  return (
    <header
      className="flex items-center"
      style={{
        // Antes era `height: 72` rígido — eso ignoraba el safe-area-inset-top
        // de iOS con notch (el contenido quedaba debajo del notch) y no se
        // adaptaba al alto del cluster cuando el viewport era angosto.
        // Ahora minHeight + padding simétrico permiten que la barra crezca
        // si el dispositivo tiene notch, y se mantenga en 72px en desktop.
        paddingTop: 'max(env(safe-area-inset-top), 14px)',
        paddingBottom: 14,
        paddingRight: 'max(env(safe-area-inset-right), 18px)',
        minHeight: 72,
        flexShrink: 0,
        color: '#fff',
        gap: 12,
        position: 'relative',
        zIndex: 30,
      }}
    >
      {/* Elbow desktop — bloque del codo con logo + Salonika.
          Mismo ancho que el sidebar (236px ó 72px collapsed) para que la marca
          se alinee verticalmente con los items debajo. En collapsed la S queda
          centrada en una columna de 72px, idéntica al tamaño/posición de los
          iconos del rail debajo (44x44 centrados). */}
      {!hideElbow && (
        <div
          className="hidden lg:flex items-center"
          style={{
            width: sidebarCollapsed ? 72 : 236,
            flexShrink: 0,
            height: '100%',
            // padding-left 19 en expanded para que la S (44x44) tenga su
            // centro en el mismo x que el icono de cada item del sidebar
            // (aside padding-left 16 + item padding-left 15 + icon 20/2 = 41).
            padding: sidebarCollapsed ? 0 : '0 19px',
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            gap: 12,
            transition: 'width 220ms cubic-bezier(.4,0,.2,1), padding 220ms',
          }}
        >
          {sidebarCollapsed ? (
            /* Colapsado: el mismo icono de app del login (S + T●M) en 44px. */
            <SalonikaTomIcon size={44} style={{ borderRadius: 13, boxShadow: 'none' }} />
          ) : (
            /* Expandido: lockup tom | salonika (con su símbolo) + subtítulo. */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <TomSalonikaLockup size={26} />
              <span
                style={{
                  fontSize: 11, fontWeight: 600,
                  color: 'rgba(255,255,255,.72)',
                  whiteSpace: 'nowrap', lineHeight: 1.1,
                }}
              >
                {t('app.subtitulo')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Logo inline mobile — sustituye al codo desktop. Tap abre el drawer.
          En viewports muy angostos (<sm = 640) ocultamos "Salonika" para que
          el cluster de la derecha (bell + avatar) tenga espacio. */}
      <button
        onClick={onMobileLogoTap}
        disabled={!onMobileLogoTap}
        className="lg:hidden flex items-center press"
        aria-label={onMobileLogoTap ? t('top.abrirMenu') : 'Salonika'}
        style={{
          gap: 11,
          paddingLeft: 'max(env(safe-area-inset-left), 18px)',
          paddingRight: 4,
          background: 'transparent', border: 0,
          color: '#fff', cursor: onMobileLogoTap ? 'pointer' : 'default',
          fontFamily: 'inherit',
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        {/* Angosto (<sm): solo el icono S+TOM. Más ancho: el lockup T●M | Salonika
            igual que en desktop. */}
        <SalonikaTomIcon size={38} className="sm:hidden" style={{ borderRadius: 11, boxShadow: 'none' }} />
        <span className="hidden sm:inline">
          <TomSalonikaLockup size={20} />
        </span>
      </button>

      {/* Spacer — equivalente al .sp del design */}
      <div style={{ flex: 1 }} />

      {/* Search button — usa GlobalSearch fill con override CSS (.topbar-cool-search)
          para que se vea translúcido sobre el azul. */}
      <div
        className="topbar-cool-search hidden sm:flex"
        style={{ width: 'min(300px, 34%)', flexShrink: 0 }}
      >
        <GlobalSearch fill />
      </div>

      <div style={{ flex: 1 }} />

      {/* Cluster derecho: [search-icon mobile] + bell + sede + avatar.
          A diferencia del C, NO van dentro de un pill — son ítems sueltos
          (bell con bg propio, sede pill propio, avatar). En mobile (<lg)
          también aparece el botón de búsqueda como ícono (lupa). */}
      <div className="inline-flex items-center" style={{ gap: 10, flexShrink: 0 }}>
        {/* Search icon — solo mobile, sustituye al search pill del content. */}
        <div className="lg:hidden">
          <GlobalSearch iconOnly />
        </div>

        {/* Conexión con el servidor (modo contingencia): siempre visible. */}
        <IndicadorConexion />

        {/* Bell */}
        <div ref={notifRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setNotifOpen(o => !o)}
            className="press"
            aria-label={t('top.notificaciones')}
            style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'rgba(255,255,255,.16)', color: '#fff',
              border: 0, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
            }}
          >
            <Bell className="w-[18px] h-[18px]" />
            {unread > 0 && (
              <span
                aria-hidden="true"
                className={urgentReserva ? 'bell-dot-blink' : ''}
                style={{
                  position: 'absolute', top: 8, right: 9,
                  width: urgentReserva ? 10 : 8,
                  height: urgentReserva ? 10 : 8,
                  borderRadius: 999,
                  // Roja+pulso cuando hay reserva inminente; ámbar normal si no.
                  background: urgentReserva ? 'var(--danger)' : 'var(--warn)',
                  border: '2px solid #1257A8',
                }}
              />
            )}
          </button>
          {notifOpen && (
            <NotifPanel
              notifications={notifications}
              reservaAlerts={reservaAlerts}
              onDismissNotification={onDismissNotification}
              onDismissAllNotifications={onDismissAllNotifications}
              onDismissReserva={onDismissReserva}
              onGoToInventory={onGoToInventory ? () => { setNotifOpen(false); onGoToInventory(); } : undefined}
              onGoToReservations={onGoToReservations ? () => { setNotifOpen(false); onGoToReservations(); } : undefined}
            />
          )}
        </div>

        {/* Sede pill — visible siempre.
            Mobile: icono Building2 + iniciales (PW, EM, etc.) en pill compacto.
            sm+: icono + label completo ("Park Way"). */}
        <div ref={branchRef} style={{ position: 'relative' }}>
          <button
            onClick={() => canChangeBranch && setBranchOpen(o => !o)}
            disabled={!canChangeBranch}
            className="press"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 14px',
              background: 'rgba(255,255,255,.14)',
              border: '1.5px solid rgba(255,255,255,.24)',
              borderRadius: 999,
              fontSize: 13.5, fontWeight: 700, color: '#fff',
              fontFamily: 'inherit',
              cursor: canChangeBranch ? 'pointer' : 'default',
              whiteSpace: 'nowrap',
            }}
            title={currentBranch}
            aria-label={t('top.sede', { sede: currentBranch })}
          >
            <Building2 className="w-[15px] h-[15px]" style={{ color: 'rgba(255,255,255,.85)' }} />
            {/* Mobile: iniciales. sm+: label completo. */}
            <span className="sm:hidden tabular-nums">{branchInitials(currentBranch)}</span>
            <span className="hidden sm:inline">{branchLabel(currentBranch)}</span>
            {canChangeBranch && <ChevronDown className="w-3 h-3" />}
          </button>
          {branchOpen && (
            <BranchMenu
              branches={branches}
              currentBranch={currentBranch}
              onPick={(b) => { onBranchChange(b); setBranchOpen(false); }}
            />
          )}
        </div>

        {/* Moneda distinta a COP (oct-2026): aviso fijo de que FED, Bold y
            Domicilios MU están desconectados. Con COP no se pinta nada. */}
        {!operaColombia && (
          <span
            title={t('top.monedaChipTitulo', { moneda: moneda.codigo })}
            className="hidden sm:inline-flex items-center rounded-full bg-amber-100 text-amber-900 ring-1 ring-amber-300 px-3 py-1.5 text-[12.5px] font-extrabold whitespace-nowrap"
          >
            {t('top.monedaChip', { moneda: moneda.codigo })}
          </span>
        )}

        {/* Avatar */}
        <div ref={userRef} style={{ position: 'relative' }}>
          <button
            onClick={() => { setUserOpen(o => !o); setRoleOpen(false); }}
            aria-label={t('top.menuUsuario')}
            style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(255,255,255,.22)',
              border: '1.5px solid rgba(255,255,255,.32)',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 13, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'inherit',
            }}
          >
            {initials(user?.name)}
          </button>
          {userOpen && (
            <UserMenu
              user={user}
              isDev={isDev}
              isFullscreen={isFullscreen}
              roleOpen={roleOpen}
              setRoleOpen={setRoleOpen}
              onToggleFullscreen={() => { toggleFullscreen(); setUserOpen(false); }}
              onLogout={() => { onLogout(); setUserOpen(false); }}
              onSwitchRole={(id) => void switchRole(id)}
            />
          )}
        </div>
      </div>
    </header>
  );
}

// ---------- Helpers (mismos paneles que en TopBarC, reutilizados aquí) ----------

function NotifPanel({
  notifications, reservaAlerts,
  onDismissNotification, onDismissAllNotifications, onDismissReserva,
  onGoToInventory, onGoToReservations,
}: {
  notifications: InventoryAlert[];
  reservaAlerts: ReservaAlert[];
  onDismissNotification?: (id: number) => void;
  onDismissAllNotifications?: () => void;
  onDismissReserva?: (id: number) => void;
  onGoToInventory?: () => void;
  onGoToReservations?: () => void;
}) {
  const total = notifications.length + reservaAlerts.length;
  const { t } = useI18n();
  return (
    <div
      style={{
        position: 'absolute', top: 'calc(100% + 10px)', right: 0,
        width: 'min(340px, calc(100vw - 24px))', background: 'var(--surface)', color: 'var(--ink)',
        border: '1.5px solid var(--line)', borderRadius: 14,
        boxShadow: 'var(--sh-lg)', overflow: 'hidden', zIndex: 50,
      }}
    >
      <div className="flex items-center" style={{ padding: '12px 14px', borderBottom: '1.5px solid var(--line)' }}>
        <b style={{ fontSize: 14, fontWeight: 800 }}>{t('top.notificaciones')}</b>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--muted-ink)' }}>{total}</span>
      </div>
      <div style={{ maxHeight: 340, overflowY: 'auto' }}>
        {total === 0 && (
          <div style={{ padding: '20px 14px', color: 'var(--muted-ink)', fontSize: 13.5, fontWeight: 600, textAlign: 'center' }}>
            {t('top.sinAlertas')}
          </div>
        )}
        {reservaAlerts.length > 0 && (
          <>
            <SectionLabel>{t('top.reservasProximas')}</SectionLabel>
            {reservaAlerts.slice(0, 6).map(r => {
              const hora = new Date(r.fechaHora).toLocaleTimeString('es-CO', { timeZone: 'America/Bogota',  hour: '2-digit', minute: '2-digit' });
              const cuando = r.minutosHasta < 0
                ? t('top.haceMin', { n: Math.abs(r.minutosHasta) })
                : r.minutosHasta === 0 ? t('top.ahora') : t('top.enMin', { n: r.minutosHasta });
              const inmediata = r.minutosHasta >= -15 && r.minutosHasta <= 15;
              return (
                <div
                  key={r.id}
                  className="flex items-start"
                  style={{
                    padding: '10px 14px', gap: 8,
                    fontSize: 13, fontWeight: 600,
                    borderBottom: '1px dashed var(--line)',
                  }}
                >
                  <span style={{ color: inmediata ? 'var(--warn)' : 'var(--blue-600)', marginTop: 2 }}>●</span>
                  <button
                    onClick={onGoToReservations}
                    disabled={!onGoToReservations}
                    className="press"
                    style={{
                      flex: 1, background: 'transparent', border: 0,
                      cursor: onGoToReservations ? 'pointer' : 'default',
                      textAlign: 'left', fontFamily: 'inherit',
                      color: 'var(--ink)', padding: 0,
                      minWidth: 0,
                    }}
                  >
                    <b>{r.cliente}</b>
                    <span style={{ marginLeft: 6, color: 'var(--muted-ink)', fontWeight: 600 }}>
                      · {r.personas} {t('top.pers')} · {hora} ({cuando})
                    </span>
                  </button>
                  {onDismissReserva && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onDismissReserva(r.id); }}
                      aria-label={t('top.descartarReserva')}
                      style={{
                        border: 0, background: 'transparent', cursor: 'pointer',
                        color: 'var(--muted-2)', fontSize: 18, lineHeight: 1,
                        padding: '0 2px', marginTop: -2, flexShrink: 0,
                      }}
                    >×</button>
                  )}
                </div>
              );
            })}
          </>
        )}
        {notifications.length > 0 && (
          <>
            <SectionLabel withTopBorder={reservaAlerts.length > 0}>{t('top.inventarioBajo')}</SectionLabel>
            {notifications.slice(0, 8).map(n => (
              <div key={n.id} className="flex items-start" style={{
                padding: '10px 14px', gap: 8, fontSize: 13, fontWeight: 600,
                borderBottom: '1px dashed var(--line)',
              }}>
                <span style={{ color: n.severidad === 'agotado' ? 'var(--danger)' : 'var(--warn)' }}>●</span>
                <span style={{ flex: 1, color: 'var(--ink)' }}>
                  <b>{n.nombre}</b>
                  <span style={{ marginLeft: 6, color: 'var(--muted-ink)', fontWeight: 600 }}>
                    {n.cantidad} {n.unidad} · {t('top.min')} {n.stock_minimo}
                  </span>
                </span>
                {onDismissNotification && (
                  <button
                    onClick={() => onDismissNotification(n.id)}
                    aria-label={t('top.descartar')}
                    style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--muted-2)' }}
                  >×</button>
                )}
              </div>
            ))}
          </>
        )}
      </div>
      <div className="flex" style={{ borderTop: '1.5px solid var(--line)', padding: 8, gap: 6 }}>
        {onDismissAllNotifications && notifications.length > 0 && (
          <button
            onClick={onDismissAllNotifications}
            className="press"
            style={{
              flex: 1, padding: '8px 10px', border: 0,
              background: 'var(--bg-app)', borderRadius: 8,
              fontSize: 12.5, fontWeight: 700, cursor: 'pointer', color: 'var(--ink)',
            }}
          >{t('top.limpiarInventario')}</button>
        )}
        {onGoToInventory && (
          <button
            onClick={onGoToInventory}
            className="press"
            style={{
              flex: 1, padding: '8px 10px', border: 0,
              background: 'var(--blue-600)', color: '#fff', borderRadius: 8,
              fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >{t('top.verInventario')}</button>
        )}
      </div>
    </div>
  );
}

function SectionLabel({ children, withTopBorder }: { children: React.ReactNode; withTopBorder?: boolean }) {
  return (
    <div style={{
      padding: withTopBorder ? '12px 14px 4px' : '10px 14px 4px',
      fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em',
      textTransform: 'uppercase', color: 'var(--muted-2)',
      borderTop: withTopBorder ? '1px solid var(--line)' : undefined,
    }}>{children}</div>
  );
}

function BranchMenu({ branches, currentBranch, onPick }: {
  branches: string[]; currentBranch: string; onPick: (b: string) => void;
}) {
  return (
    <div
      style={{
        position: 'absolute', top: 'calc(100% + 10px)', right: 0,
        minWidth: 220, maxWidth: 'calc(100vw - 24px)', background: 'var(--surface)', color: 'var(--ink)',
        border: '1.5px solid var(--line)', borderRadius: 14,
        boxShadow: 'var(--sh-lg)', overflow: 'hidden', zIndex: 50,
      }}
    >
      <div style={{ padding: 6 }}>
        {branches.map(b => {
          const active = b === currentBranch;
          return (
            <button
              key={b}
              onClick={() => onPick(b)}
              className="press"
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10,
                border: 0, cursor: 'pointer',
                background: active ? 'var(--blue-50)' : 'transparent',
                color: active ? 'var(--blue-700)' : 'var(--ink)',
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
                textAlign: 'left',
              }}
            >
              <Store className="w-4 h-4" />
              {branchLabel(b)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function UserMenu({
  user, isDev, isFullscreen, roleOpen, setRoleOpen,
  onToggleFullscreen, onLogout, onSwitchRole,
}: {
  user: any;
  isDev: boolean;
  isFullscreen: boolean;
  roleOpen: boolean;
  setRoleOpen: (v: boolean) => void;
  onToggleFullscreen: () => void;
  onLogout: () => void;
  onSwitchRole: (id: string) => void;
}) {
  const { t, idioma, idiomasHabilitados, cambiarIdioma } = useI18n();
  return (
    <div
      style={{
        position: 'absolute', top: 'calc(100% + 10px)', right: 0,
        minWidth: 240, maxWidth: 'calc(100vw - 24px)', background: 'var(--surface)', color: 'var(--ink)',
        border: '1.5px solid var(--line)', borderRadius: 14,
        boxShadow: 'var(--sh-lg)', overflow: 'hidden', zIndex: 50,
      }}
    >
      <div style={{ padding: '12px 14px', borderBottom: '1.5px solid var(--line)' }}>
        <div style={{ fontSize: 14, fontWeight: 800 }}>{user?.name}</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted-ink)', marginTop: 2 }}>
          {ROLE_LABEL[user?.role || ''] ? t(ROLE_LABEL[user?.role || '']) : user?.role}
        </div>
      </div>
      <div style={{ padding: 6 }}>
        {/* "Cambiar layout" eliminado — solo existe Layout Cool ahora. */}
        <Item
          icon={isFullscreen ? Minimize2 : Maximize2}
          label={isFullscreen ? t('top.salirPantallaCompleta') : t('top.pantallaCompleta')}
          onClick={onToggleFullscreen}
        />
        {/* Idioma (oct-2026): solo si el desarrollador habilitó más de uno en
            Features. Cambia cuando el backend confirma (usuarios.idioma). */}
        {idiomasHabilitados.length > 1 && (
          <div className="px-3 pt-1.5 pb-2">
            <div className="mb-1.5 text-[10.5px] font-extrabold uppercase tracking-[.06em]" style={{ color: 'var(--muted-2)' }}>
              {t('top.idioma')}
            </div>
            <div className="flex gap-1.5">
              {IDIOMAS.filter(i => idiomasHabilitados.includes(i.codigo)).map(i => (
                <button
                  key={i.codigo}
                  type="button"
                  onClick={() => { cambiarIdioma(i.codigo).catch(() => {}); }}
                  aria-pressed={i.codigo === idioma}
                  className={`press flex-1 rounded-lg px-2.5 py-1.5 text-[12.5px] font-bold border-0 cursor-pointer ${
                    i.codigo === idioma ? 'bg-primary text-primary-foreground' : 'bg-secondary text-primary'
                  }`}
                >
                  {i.nombre}
                </button>
              ))}
            </div>
          </div>
        )}
        {isDev && (
          <Item
            icon={RefreshCw}
            label={roleOpen ? t('top.ocultarCambiarRol') : t('top.cambiarRol')}
            onClick={() => setRoleOpen(!roleOpen)}
          />
        )}
        {isDev && roleOpen && (
          <div style={{ padding: '4px 6px 4px 32px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Object.entries(QUICK_LOGIN_CREDS).map(([id, c]) => (
              <button
                key={id}
                onClick={() => onSwitchRole(id)}
                style={{
                  padding: '6px 8px', borderRadius: 8, border: 0,
                  background: 'transparent', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
                  textAlign: 'left', color: 'var(--muted-ink)',
                }}
              >{c.label || id}</button>
            ))}
          </div>
        )}
        <Item icon={LogOut} label={t('top.cerrarSesion')} onClick={onLogout} danger />
      </div>
    </div>
  );
}

function Item({ icon: Icon, label, onClick, danger }: {
  icon: any; label: string; onClick: () => void; danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className="press"
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px', borderRadius: 10,
        border: 0, background: 'transparent',
        fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
        color: danger ? 'var(--danger-ink, #A6322B)' : 'var(--ink)',
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}
